// src/components/JIG/JIG.js - Version avec deux dates
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Title
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import apiService from '../../API/ApiService';

// Enregistrer les composants Chart.js
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Title
);

// === FONCTIONS UTILITAIRES ===
const addTimeToDate = (dateString, startOfDay = true) => {
  if (!dateString) return dateString;
  if (dateString.includes('T')) return dateString;
  return startOfDay ? `${dateString}T00:00:00` : `${dateString}T23:59:59`;
};

const cleanStepName = (name) => {
  if (!name || typeof name !== 'string') return "";
  return name.toLowerCase().trim().replace(/\s+/g, "").replace(/[_-]/g, "");
};

const normalizePanneName = (panneName) => {
  if (!panneName || typeof panneName !== 'string') return panneName;
  
  const map = {
    leftStickX: 'leftStick',
    leftStickY: 'leftStick',
    rightStickX: 'rightStick',
    rightStickY: 'rightStick',
    leftStickXmin: 'leftStick',
    leftStickXmax: 'leftStick',
    leftStickYmin: 'leftStick',
    leftStickYmax: 'leftStick',
    rightStickXmin: 'rightStick',
    rightStickXmax: 'rightStick',
    rightStickYmin: 'rightStick',
    rightStickYmax: 'rightStick',
    playStation: 'psButton',
    dynamicJoystickOK: 'dynamicJoystick',
    noSpecialCaseErrorFound: 'specialCase',
    micAudioCheck: 'microphone',
    bluetooth: 'bluetooth'
  };

  return map[panneName] || panneName;
};

// Fonction pour obtenir la date à utiliser (priorité à processed_date)
const getSessionDate = (session, forSorting = true) => {
  return session?.processed_date || session?.startDate || '';
};

// FONCTION PRINCIPALE : Extraire UNE SEULE panne du failureStep
const getRealPanneFromSession = (session) => {
  if (!session) return 'Inconnu';
  
  if (session.globalStatus === 'OK') {
    return 'PASS';
  }
  
  if (!session.testTracking) {
    return 'NO_DATA';
  }
  
  try {
    const testData = typeof session.testTracking === 'string' 
      ? JSON.parse(session.testTracking) 
      : session.testTracking;
    
    if (!testData || typeof testData !== 'object') {
      return 'INVALID_DATA';
    }
    
    const failureStep = testData.failureStep;
    if (!failureStep) {
      return 'NO_FAILURE_STEP';
    }
    
    let stepData = testData[failureStep];
    
    if (!stepData || typeof stepData !== 'object') {
      const cleanStep = cleanStepName(failureStep);
      for (const key in testData) {
        if (cleanStepName(key) === cleanStep && testData[key] && typeof testData[key] === 'object') {
          stepData = testData[key];
          break;
        }
      }
    }
    
    if (!stepData || typeof stepData !== 'object') {
      return 'STEP_DATA_MISSING';
    }
    
    const cleanFailureStep = cleanStepName(failureStep);
    
    if (cleanFailureStep === 'stepone') {
      for (const key in stepData) {
        if (stepData[key] === true) {
          return normalizePanneName(key);
        }
      }
    } else {
      for (const key in stepData) {
        if (stepData[key] === null) {
          return normalizePanneName(key);
        }
      }
      
      for (const key in stepData) {
        if (stepData[key] === false) {
          return normalizePanneName(key);
        }
      }
    }
    
    return 'NO_FAILURE_DETECTED';
    
  } catch (error) {
    console.error("❌ Erreur extraction panne:", error, session.realSerialNumber);
    return 'EXTRACTION_ERROR';
  }
};

// FONCTION CORRIGÉE : Extraire TOUTES les pannes d'un failureStep (spécialement pour "Other")
const extractPannesFromFailureStepForOther = (session) => {
  if (!session || session.globalStatus !== 'KO' || session.symptome_description !== 'Other') {
    return [];
  }
  
  if (!session.testTracking) {
    return [];
  }
  
  try {
    const testData = typeof session.testTracking === 'string' 
      ? JSON.parse(session.testTracking) 
      : session.testTracking;
    
    if (!testData || typeof testData !== 'object') {
      return [];
    }
    
    const failureStep = testData.failureStep;
    if (!failureStep) {
      return [];
    }
    
    let stepData = testData[failureStep];
    
    if (!stepData || typeof stepData !== 'object') {
      const cleanStep = cleanStepName(failureStep);
      for (const key in testData) {
        if (cleanStepName(key) === cleanStep && testData[key] && typeof testData[key] === 'object') {
          stepData = testData[key];
          break;
        }
      }
    }
    
    if (!stepData || typeof stepData !== 'object') {
      return [];
    }
    
    const pannes = [];
    const cleanFailureStep = cleanStepName(failureStep);
    
    if (cleanFailureStep === 'stepone') {
      for (const key in stepData) {
        if (stepData[key] === true) {
          const panne = normalizePanneName(key);
          if (!pannes.includes(panne)) {
            pannes.push(panne);
          }
        }
      }
    } else if (cleanFailureStep === 'steptwo') {
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          const panne = normalizePanneName(key);
          if (!pannes.includes(panne)) {
            pannes.push(panne);
          }
        }
      }
    } else if (cleanFailureStep === 'stepthree') {
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          const panne = normalizePanneName(key);
          if (!pannes.includes(panne)) {
            pannes.push(panne);
          }
        }
      }
    } else if (cleanFailureStep === 'stepfour') {
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          const panne = normalizePanneName(key);
          if (!pannes.includes(panne)) {
            pannes.push(key);
          }
        }
      }
    } else if (cleanFailureStep === 'stepfive') {
      if (stepData.noSpecialCaseErrorFound !== true) {
        pannes.push('specialCase');
      }
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          if (key !== 'noSpecialCaseErrorFound') {
            const panne = normalizePanneName(key);
            if (!pannes.includes(panne)) {
              pannes.push(panne);
            }
          }
        }
      }
    } else if (cleanFailureStep === 'stepsix') {
      if (stepData.micAudioCheck !== true) {
        pannes.push('microphone');
      }
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          if (key !== 'micAudioCheck') {
            const panne = normalizePanneName(key);
            if (!pannes.includes(panne)) {
              pannes.push(panne);
            }
          }
        }
      }
    } else if (cleanFailureStep === 'stepseven') {
      if (stepData.bluetooth !== true) {
        pannes.push('bluetooth');
      }
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          if (key !== 'bluetooth') {
            const panne = normalizePanneName(key);
            if (!pannes.includes(panne)) {
              pannes.push(panne);
            }
          }
        }
      }
    } else {
      for (const key in stepData) {
        if (stepData[key] === null || stepData[key] === false) {
          const panne = normalizePanneName(key);
          if (!pannes.includes(panne)) {
            pannes.push(panne);
          }
        }
      }
    }
    
    if (pannes.length === 0 && session.globalStatus === 'KO') {
      return ['NO_PANNE_DETECTED_IN_STEP'];
    }
    
    return pannes;
    
  } catch (error) {
    console.error("❌ Erreur extraction pannes failureStep:", error, session.realSerialNumber);
    return ['EXTRACTION_ERROR'];
  }
};

// Fonction pour éliminer les doublons de SN - MODIFIÉE pour utiliser processed_date
const filterUniqueSessionsBySN = (sessions) => {
  const seenSNs = new Set();
  const uniqueSessions = [];
  
  // Trier par processed_date décroissante (ou startDate si processed_date n'existe pas)
  const sortedSessions = [...sessions].sort((a, b) => 
    new Date(getSessionDate(b)) - new Date(getSessionDate(a))
  );
  
  sortedSessions.forEach(session => {
    const sn = session.realSerialNumber || session.deviceSerialNumber;
    if (sn && !seenSNs.has(sn)) {
      seenSNs.add(sn);
      uniqueSessions.push(session);
    }
  });
  
  return uniqueSessions;
};

// Fonction pour obtenir les pannes "Other" formatées pour l'affichage
const getOtherPannesFormatted = (session) => {
  if (!session || session.globalStatus !== 'KO' || session.symptome_description !== 'Other') {
    return 'N/A';
  }
  
  const pannes = extractPannesFromFailureStepForOther(session);
  
  if (pannes.length === 0) {
    return 'NO_PANNE_IN_FAILURE_STEP';
  }
  
  return pannes.join(', ');
};

// Fonction pour calculer les pannes "Other" sans doublons SN
const calculateOtherPannesFromSessions = (sessions) => {
  const otherMap = {};
  const seenSNs = new Set();
  
  sessions.forEach(s => {
    if (!s || s.globalStatus !== 'KO' || s.symptome_description !== 'Other') return;
    
    const sn = s.realSerialNumber || s.deviceSerialNumber;
    if (!sn || seenSNs.has(sn)) return;
    
    seenSNs.add(sn);
    
    const pannes = extractPannesFromFailureStepForOther(s);
    
    if (pannes.length > 0) {
      pannes.forEach(panne => {
        if (panne && panne !== 'PASS' && panne !== 'NO_FAILURE_DETECTED' && 
            panne !== 'NO_DATA' && panne !== 'INVALID_DATA' && 
            panne !== 'NO_FAILURE_STEP' && panne !== 'STEP_DATA_MISSING' && 
            panne !== 'EXTRACTION_ERROR' && panne !== 'Inconnu') {
          otherMap[panne] = (otherMap[panne] || 0) + 1;
        }
      });
    } else {
      otherMap['NO_PANNE_DETECTED_IN_STEP'] = (otherMap['NO_PANNE_DETECTED_IN_STEP'] || 0) + 1;
    }
  });
  
  return Object.entries(otherMap)
    .map(([panne, count]) => ({ panne, count }))
    .sort((a, b) => b.count - a.count);
};

// Fonction pour calculer les pannes désignées sans doublons SN
const calculateDesignatedPannesStats = (sessions) => {
  const pannesMap = {};
  let totalDesignatedCount = 0;
  const seenSNs = new Set();
  
  sessions.forEach(s => {
    if (s.globalStatus !== 'KO' || !s.panne_descriptions) return;
    
    const sn = s.realSerialNumber || s.deviceSerialNumber;
    if (!sn || seenSNs.has(sn)) return;
    
    seenSNs.add(sn);
    
    const descriptions = s.panne_descriptions.split(' , ').filter(d => d.trim() !== '');
    
    descriptions.forEach(desc => {
      const cleanDesc = desc.trim();
      if (!pannesMap[cleanDesc]) {
        pannesMap[cleanDesc] = {
          panne: cleanDesc,
          count: 0,
          otherCount: 0,
          sessions: []
        };
      }
      
      pannesMap[cleanDesc].count += 1;
      pannesMap[cleanDesc].sessions.push(s);
      totalDesignatedCount += 1;
      
      if (s.symptome_description === 'Other') {
        pannesMap[cleanDesc].otherCount += 1;
      }
    });
  });
  
  const uniqueKO = Array.from(new Set(sessions
    .filter(s => s.globalStatus === 'KO')
    .map(s => s.realSerialNumber || s.deviceSerialNumber)
    .filter(sn => sn)
  )).length;
  
  return {
    stats: Object.values(pannesMap)
      .map(item => ({
        ...item,
        percentage: uniqueKO > 0 ? parseFloat(((item.count / uniqueKO) * 100).toFixed(1)) : 0,
        otherPercentage: item.count > 0 ? parseFloat(((item.otherCount / item.count) * 100).toFixed(1)) : 0
      }))
      .sort((a, b) => b.count - a.count),
    totalCount: totalDesignatedCount,
    uniqueCount: Object.keys(pannesMap).length
  };
};

const formatOperator = (operator) => {
  if (!operator || operator === "null" || operator === "undefined" || 
      typeof operator !== 'string' || operator.trim() === "") {
    return "n/a";
  }
  return operator.trim();
};

// Fonction pour formater une date
const formatDate = (dateString, showTime = true) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    if (showTime) {
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  } catch (e) {
    return 'N/A';
  }
};

// Composant Modal pour les détails du testeur - MODIFIÉ pour afficher les deux dates
const TesterDetailsModal = ({ tester, onClose }) => {
  if (!tester) return null;
  
  const sortedSessions = [...tester.sessions].sort((a, b) => 
    new Date(getSessionDate(b)) - new Date(getSessionDate(a))
  );
  
  const topPannesData = tester.topPannes.slice(0, 5);
  const otherPannesData = tester.otherPannesStats.slice(0, 5);
  
  // Calculer la période d'activité basée sur processed_date
  const getActivityPeriod = () => {
    if (!tester.firstTest || !tester.lastTest) return 'N/A';
    return `${formatDate(tester.firstTest, false)} - ${formatDate(tester.lastTest, false)}`;
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Détails du Testeur: {tester.name}</h2>
            <p className="modal-subtitle">
              {tester.total} sessions | {tester.ok} PASS | {tester.ko} FAIL | Taux: {tester.rate}%
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-label">Période d'activité</span>
              <span className="stat-value">
                {getActivityPeriod()}
              </span>
            </div>
          </div>
          
          <div className="tester-details-grid">
            <div className="detail-section">
              <h3>Top 5 Pannes Déclarées</h3>
              {topPannesData.length > 0 ? (
                <table className="data-table small">
                  <thead>
                    <tr>
                      <th>Panne</th>
                      <th className="text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPannesData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.panne}</td>
                        <td className="text-right">{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">Aucune panne</p>
              )}
            </div>
            
            <div className="detail-section">
              <h3>Top 5 Pannes "Other" Détectées</h3>
              {otherPannesData.length > 0 ? (
                <table className="data-table small">
                  <thead>
                    <tr>
                      <th>Panne Détectée</th>
                      <th className="text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherPannesData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.panne}</td>
                        <td className="text-right">{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">Aucune panne "Other"</p>
              )}
            </div>
          </div>
          
          <div className="detail-section full-width">
            <h3>Dernières Sessions ({sortedSessions.length})</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date Start</th>
                    <th>Date Processed</th>
                    <th>SN</th>
                    <th>Résultat</th>
                    <th>Panne Déclarée</th>
                    <th>Panne Désignée</th>
                    <th>Panne Détectée</th>
                    <th>Pannes "Other" Détectées</th>
                    <th>Failure Step</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSessions.slice(0, 10).map((session, index) => {
                    const panne = getRealPanneFromSession(session);
                    const result = panne === 'PASS' ? 'PASS' : 'FAIL';
                    const pannesDesignees = session.panne_descriptions ? 
                      session.panne_descriptions.split(' , ').filter(d => d.trim() !== '') : [];
                    const otherPannes = session.symptome_description === 'Other' ? 
                      getOtherPannesFormatted(session) : 'N/A';
                    
                    let failureStepName = '';
                    if (session.testTracking) {
                      try {
                        const td = typeof session.testTracking === 'string' 
                          ? JSON.parse(session.testTracking) 
                          : session.testTracking;
                        failureStepName = td?.failureStep || '';
                      } catch (e) {
                        console.error("Erreur parsing testTracking:", e);
                      }
                    }
                    
                    return (
                      <tr key={index}>
                        <td>{formatDate(session.startDate)}</td>
                        <td>{formatDate(session.processed_date)}</td>
                        <td className="serial-cell">{session.realSerialNumber || 'N/A'}</td>
                        <td className={result === 'PASS' ? 'pass' : 'fail'}>
                          <span className={`status-badge ${result}`}>{result}</span>
                        </td>
                        <td>{session.symptome_description || 'N/A'}</td>
                        <td>{pannesDesignees.join(', ') || 'N/A'}</td>
                        <td>{panne}</td>
                        <td className="other-cell">
                          {session.symptome_description === 'Other' ? otherPannes : 'N/A'}
                        </td>
                        <td>{failureStepName || 'N/A'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour afficher les détails d'une recherche par SN - MODIFIÉ pour afficher les deux dates
const SNSearchModal = ({ searchResults, onClose }) => {
  if (!searchResults || searchResults.length === 0) return null;

  const totalResults = searchResults.length;
  const passResults = searchResults.filter(s => s.globalStatus === 'OK').length;
  const failResults = searchResults.filter(s => s.globalStatus === 'KO').length;
  const passRate = totalResults > 0 ? ((passResults / totalResults) * 100).toFixed(1) : 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Recherche par Numéro de Série</h2>
            <p className="modal-subtitle">
              {searchResults.length} session(s) trouvée(s) | 
              PASS: {passResults} | FAIL: {failResults} | Taux PASS: {passRate}%
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="search-stats">
            <div className="stats-summary">
              <div className="stat-item">
                <span className="stat-label">Total sessions</span>
                <span className="stat-value">{totalResults}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">PASS</span>
                <span className="stat-value pass">{passResults}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">FAIL</span>
                <span className="stat-value fail">{failResults}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Taux PASS</span>
                <span className="stat-value">{passRate}%</span>
              </div>
            </div>
          </div>

          <div className="table-container">
            <table className="data-table detailed">
              <thead>
                <tr>
                  <th>Date Start</th>
                  <th>Date Processed</th>
                  <th>SN</th>
                  <th>Testeur</th>
                  <th>Opérateur</th>
                  <th>Résultat</th>
                  <th>Panne déclarée</th>
                  <th>Panne désignée</th>
                  <th>Panne détectée</th>
                  <th>Pannes "Other" Détectées</th>
                  <th>Failure Step</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((session, index) => {
                  const panne = getRealPanneFromSession(session);
                  let failureStepName = '';
                  
                  if (session.testTracking) {
                    try {
                      const td = typeof session.testTracking === 'string' 
                        ? JSON.parse(session.testTracking) 
                        : session.testTracking;
                      failureStepName = td?.failureStep || '';
                    } catch (e) {
                      console.error("Erreur parsing testTracking:", e);
                    }
                  }
                  
                  let resultat = panne === 'PASS' ? 'PASS' : 'FAIL';
                  let displayPanne = panne;
                  
                  if (panne === 'NO_DATA') displayPanne = 'Pas de données test';
                  else if (panne === 'INVALID_DATA') displayPanne = 'Données test invalides';
                  else if (panne === 'NO_FAILURE_DETECTED') displayPanne = 'Pas de panne détectée';
                  else if (panne === 'EXTRACTION_ERROR') displayPanne = 'Erreur extraction';
                  else if (panne === 'NO_FAILURE_STEP') displayPanne = 'Pas de failure step';
                  else if (panne === 'STEP_DATA_MISSING') displayPanne = 'Données étape manquantes';
                  else if (panne === 'Inconnu') displayPanne = 'Inconnu';
                  else if (panne === 'NO_TESTTRACKING_FOR_DESIGNATED') displayPanne = 'N/A';
                  
                  const pannesDesignees = session.panne_descriptions ? 
                    session.panne_descriptions.split(' , ').filter(d => d.trim() !== '') : [];
                  
                  const otherPannes = session.symptome_description === 'Other' ? 
                    getOtherPannesFormatted(session) : 'N/A';
                  
                  return (
                    <tr key={index} className={index % 2 === 0 ? 'even' : 'odd'}>
                      <td>{formatDate(session.startDate)}</td>
                      <td>{formatDate(session.processed_date)}</td>
                      <td className="serial-cell">{session.realSerialNumber || session.deviceSerialNumber || ''}</td>
                      <td>{session.raspberry_id || ''}</td>
                      <td>{formatOperator(session.operator_name)}</td>
                      <td className={resultat === 'PASS' ? 'pass' : 'fail'}>
                        <span className={`status-badge ${resultat === 'PASS' ? 'pass' : 'fail'}`}>
                          {resultat}
                        </span>
                      </td>
                      <td>{session.symptome_description || 'N/A'}</td>
                      <td>{pannesDesignees.join(', ') || 'N/A'}</td>
                      <td className={displayPanne !== 'PASS' && displayPanne !== 'N/A' ? 'other' : ''}>
                        {displayPanne}
                      </td>
                      <td className="other-cell">
                        {session.symptome_description === 'Other' ? otherPannes : 'N/A'}
                      </td>
                      <td>{failureStepName || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant simplifié pour la cadence des opérateurs - MODIFIÉ pour utiliser processed_date
const CadenceTable = ({ cadenceStats }) => {
  return (
    <div className="cadence-container">
      <div className="section-title">Cadence des Opérateurs (SN uniques)</div>
      
      {cadenceStats.length === 0 ? (
        <div className="no-data">Aucune donnée de cadence</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Opérateur</th>
                <th className="text-right">Total Tests (SN uniques)</th>
                <th className="text-right">PASS</th>
                <th className="text-right">FAIL</th>
                <th className="text-right">Taux Succès</th>
                <th className="text-right">Cadence (tests/h)</th>
                <th className="text-right">Testeurs</th>
                <th className="text-right">Pannes "Other"</th>
                <th className="text-right">Période (processed_date)</th>
              </tr>
            </thead>
            <tbody>
              {cadenceStats.map((operator, index) => {
                const successRateClass = 
                  operator.successRate >= 95 ? 'high' : 
                  operator.successRate >= 90 ? 'medium' : 'low';
                
                const cadenceClass = 
                  operator.cadence >= 10 ? 'high' : 
                  operator.cadence >= 5 ? 'medium' : 'low';
                
                return (
                  <tr key={index} className={index % 2 === 0 ? 'even' : 'odd'}>
                    <td>{operator.operator}</td>
                    <td className="text-right">{operator.totalTests}</td>
                    <td className="text-right pass">{operator.passCount}</td>
                    <td className="text-right fail">{operator.failCount}</td>
                    <td className="text-right">
                      <span className={`rate-badge ${successRateClass}`}>
                        {operator.successRate}%
                      </span>
                    </td>
                    <td className="text-right">
                      <span className={`rate-badge ${cadenceClass}`}>
                        {operator.cadence}
                      </span>
                    </td>
                    <td className="text-right testers-count">
                      <span>{operator.testers.length}</span>
                    </td>
                    <td className="text-right">
                      {operator.otherPannes > 0 ? (
                        <span className="other-count">{operator.otherPannes}</span>
                      ) : '0'}
                    </td>
                    <td className="text-right">
                      {operator.firstTest && operator.lastTest ? (
                        <div>
                          <div className="cadence-value">
                            {operator.totalHours.toFixed(1)}h
                          </div>
                          <div className="cadence-subtext">
                            {formatDate(operator.firstTest, false)}-
                            {formatDate(operator.lastTest, false)}
                          </div>
                        </div>
                      ) : 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Composant pour les statistiques de résumé - MODIFIÉ pour afficher la période basée sur processed_date
const SummaryStats = ({ uniqueSessions, allSessions, designatedPannesStats, totalOtherCount }) => {
  // Calculer la période basée sur processed_date
  const getDatePeriod = () => {
    const dates = uniqueSessions
      .filter(s => s.processed_date || s.startDate)
      .map(s => new Date(getSessionDate(s)));
    
    if (dates.length === 0) return 'N/A';
    
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    
    return `${formatDate(minDate, false)} - ${formatDate(maxDate, false)}`;
  };

  const uniqueTotal = uniqueSessions.length;
  const uniqueOK = uniqueSessions.filter(s => s.globalStatus === 'OK').length;
  const uniqueKO = uniqueSessions.filter(s => s.globalStatus === 'KO').length;
  const uniqueOKRate = uniqueTotal > 0 ? ((uniqueOK / uniqueTotal) * 100).toFixed(1) : 0;
  
  const totalWithDuplicates = allSessions.length;
  const okWithDuplicates = allSessions.filter(s => s.globalStatus === 'OK').length;
  const koWithDuplicates = allSessions.filter(s => s.globalStatus === 'KO').length;
  
  const otherRate = uniqueKO > 0 ? ((totalOtherCount / uniqueKO) * 100).toFixed(1) : 0;
  
  return (
    <div className="summary-container">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-label">TOTAL TESTS (SN uniques)</div>
            <div className="stat-icon">📈</div>
          </div>
          <div className="stat-value">{uniqueTotal.toLocaleString()}</div>
          <div className="stat-description">
            <small>Toutes sessions: {totalWithDuplicates.toLocaleString()}</small>
            <br/>
            <small>Période: {getDatePeriod()}</small>
          </div>
        </div>
        
        <div className="stat-card pass">
          <div className="stat-header">
            <div className="stat-label">PASS (SN uniques)</div>
            <div className="stat-icon">✅</div>
          </div>
          <div className="stat-value">{uniqueOK.toLocaleString()}</div>
          <div className="stat-details">
            <div className="stat-percentage">{uniqueOKRate}%</div>
            <div className="stat-subtext">Taux de réussite</div>
          </div>
        </div>
        
        <div className="stat-card fail">
          <div className="stat-header">
            <div className="stat-label">FAIL (SN uniques)</div>
            <div className="stat-icon">❌</div>
          </div>
          <div className="stat-value">{uniqueKO.toLocaleString()}</div>
          <div className="stat-description">
            <span className="other-count">{totalOtherCount}</span> pannes "Other" détectées
          </div>
        </div>
        
        <div className="stat-card designated">
          <div className="stat-header">
            <div className="stat-label">PANNES DÉSIGNÉES</div>
            <div className="stat-icon">📋</div>
          </div>
          <div className="stat-value">{designatedPannesStats.totalCount.toLocaleString()}</div>
          <div className="stat-description">
            {designatedPannesStats.uniqueCount} types différents
            <br/>
            <small>(Calculé sans doublons SN)</small>
          </div>
        </div>
      </div>

      <div className="chart-container">
        <div className="chart-title">Répartition PASS / FAIL (SN uniques)</div>
        <div className="chart-wrapper">
          <Doughnut 
            data={{ 
              labels: ['PASS', 'FAIL'], 
              datasets: [{ 
                data: [uniqueOK, uniqueKO], 
                backgroundColor: ['#10b981', '#ef4444'], 
                borderWidth: 2,
                borderColor: '#ffffff'
              }] 
            }} 
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    font: { size: 12 },
                    padding: 15,
                    usePointStyle: true,
                    boxWidth: 8
                  }
                },
                tooltip: {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  titleFont: { size: 12 },
                  bodyFont: { size: 11 },
                  padding: 8,
                  cornerRadius: 4,
                  callbacks: {
                    label: function(context) {
                      const label = context.label || '';
                      const value = context.raw || 0;
                      const total = context.dataset.data.reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                      return `${label}: ${value} (${percentage}%)`;
                    }
                  }
                }
              },
              cutout: '70%'
            }} 
          />
        </div>
      </div>
      
      <div className="designated-summary">
        <div className="section-title">Aperçu des Pannes Désignées (SN uniques)</div>
        {designatedPannesStats.stats.length > 0 ? (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Panne Désignée</th>
                  <th className="text-right">Occurrences</th>
                  <th className="text-right">% des FAIL</th>
                </tr>
              </thead>
              <tbody>
                {designatedPannesStats.stats.slice(0, 5).map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'even' : 'odd'}>
                    <td>{item.panne}</td>
                    <td className="text-right">{item.count}</td>
                    <td className="text-right percentage">{item.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-data">Aucune panne désignée trouvée</div>
        )}
      </div>
    </div>
  );
};

// Fonction d'export Excel - MODIFIÉE pour inclure les deux dates
const exportToExcelWithPanneDescriptions = (
  sessions, 
  uniqueSessions,
  mode, 
  dateStart, 
  dateEnd, 
  topPannes, 
  otherPannesStats,
  designatedPannesStats,
  totalOtherCount
) => {
  if (sessions.length === 0) {
    alert("Aucune donnée à exporter");
    return;
  }

  try {
    const wb = XLSX.utils.book_new();

    // FEUILLE 1: Toutes les sessions
    const allRows = sessions.map(s => {
      const hasDesignatedPanne = s.panne_descriptions && 
                                s.panne_descriptions.trim() !== "" && 
                                s.id_panne !== null;
      
      const isOtherPanne = s.symptome_description === 'Other';
      
      let panneDetectee = '';
      let otherPannesDetectees = [];
      
      if (hasDesignatedPanne) {
        panneDetectee = '';
      } else if (isOtherPanne) {
        panneDetectee = '';
        otherPannesDetectees = extractPannesFromFailureStepForOther(s);
      } else {
        const extractedPanne = getRealPanneFromSession(s);
        
        if (extractedPanne === 'NO_DATA') panneDetectee = '';
        else if (extractedPanne === 'INVALID_DATA') panneDetectee = '';
        else if (extractedPanne === 'NO_FAILURE_DETECTED') panneDetectee = '';
        else if (extractedPanne === 'EXTRACTION_ERROR') panneDetectee = '';
        else if (extractedPanne === 'NO_FAILURE_STEP') panneDetectee = '';
        else if (extractedPanne === 'STEP_DATA_MISSING') panneDetectee = '';
        else if (extractedPanne === 'Inconnu') panneDetectee = '';
        else if (extractedPanne === 'NO_TESTTRACKING_FOR_DESIGNATED') panneDetectee = '';
        else if (extractedPanne === 'PASS') panneDetectee = '';
        else {
          panneDetectee = extractedPanne;
        }
      }
      
      const pannesDesignees = s.panne_descriptions ? 
        s.panne_descriptions.split(' , ').filter(d => d.trim() !== '') : [];
      
      let failureStepName = '';
      if (s.testTracking) {
        try {
          const td = typeof s.testTracking === 'string' ? JSON.parse(s.testTracking) : s.testTracking;
          failureStepName = td?.failureStep || '';
        } catch (e) {
          console.error("Erreur parsing testTracking:", e);
        }
      }
      
      let resultat = s.globalStatus === 'OK' ? 'PASS' : 'FAIL';
      
      return {
        'Date Start': s.startDate ? new Date(s.startDate).toLocaleString('fr-FR') : '',
        'Date Processed': s.processed_date ? new Date(s.processed_date).toLocaleString('fr-FR') : '',
        'Serial Number': s.realSerialNumber || s.deviceSerialNumber || '',
        'Device Serial': s.deviceSerialNumber || '',
        'Testeur': s.raspberry_id || '',
        'Opérateur': formatOperator(s.operator_name),
        'Résultat': resultat,
        'Grade': s.grade || '',
        'Failure Step': failureStepName,
        'Panne déclarée': s.symptome_description || '',
        'Panne désignée (panne_descriptions)': pannesDesignees.join('; ') || '',
        'Panne détectée (testTracking)': panneDetectee,
        'Pannes "Other" détectées (failureStep)': otherPannesDetectees.length > 0 ? 
          otherPannesDetectees.join('; ') : '',
        
        'Famille Pannes': s.famille_pannes || ''
      };
    });

    const allWs = XLSX.utils.json_to_sheet(allRows, {
      header: [
        'Date Start', 'Date Processed', 'Serial Number', 'Device Serial', 'Testeur', 'Opérateur', 
        'Résultat', 'Grade', 'Failure Step', 'Panne déclarée', 
        'Panne désignée (panne_descriptions)', 'Panne détectée (testTracking)', 
        'Pannes "Other" détectées (failureStep)', 'Famille Pannes'
      ]
    });
    XLSX.utils.book_append_sheet(wb, allWs, "Toutes sessions");

    // Statistiques
    const uniqueTotal = uniqueSessions.length;
    const uniqueOK = uniqueSessions.filter(s => s.globalStatus === 'OK').length;
    const uniqueKO = uniqueSessions.filter(s => s.globalStatus === 'KO').length;
    const uniqueOKRate = uniqueTotal > 0 ? ((uniqueOK / uniqueTotal) * 100).toFixed(1) : 0;
    const otherRate = uniqueKO > 0 ? ((totalOtherCount / uniqueKO) * 100).toFixed(1) : 0;
    
    const summaryData = [
      { 'Statistique': 'Total tests (toutes sessions)', 'Valeur': sessions.length },
      { 'Statistique': 'Total tests (SN uniques)', 'Valeur': uniqueTotal },
      { 'Statistique': 'PASS (SN uniques)', 'Valeur': uniqueOK },
      { 'Statistique': 'FAIL (SN uniques)', 'Valeur': uniqueKO },
      { 'Statistique': 'Taux PASS (SN uniques)', 'Valeur': `${uniqueOKRate}%` },
      { 'Statistique': 'Pannes "Other" détectées', 'Valeur': totalOtherCount },
      { 'Statistique': 'Taux "Other" détectées', 'Valeur': `${otherRate}% des FAIL` },
      { 'Statistique': 'Période (filtre)', 'Valeur': `${dateStart} à ${dateEnd}` },
      { 'Statistique': 'Mode', 'Valeur': mode },
      { 'Statistique': 'Généré le', 'Valeur': new Date().toLocaleString('fr-FR') },
      { 'Statistique': 'NOTE', 'Valeur': 'Les statistiques sont calculées sans doublons de SN' },
      { 'Statistique': 'NOTE 2', 'Valeur': 'Les dates utilisées pour le tri sont les processed_date (ou startDate si processed_date n\'existe pas)' }
    ];

    const statsWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, statsWs, "Résumé");

    // Feuille des pannes désignées
    if (designatedPannesStats.stats.length > 0) {
      const designatedData = designatedPannesStats.stats.map(p => ({
        'Panne Désignée': p.panne,
        'Occurrences (SN uniques)': p.count,
        '% des FAIL (SN uniques)': `${p.percentage}%`,
        'Dont "Other"': p.otherCount,
        '% "Other" dans cette panne': `${p.otherPercentage}%`
      }));
      
      const designatedWs = XLSX.utils.json_to_sheet(designatedData);
      XLSX.utils.book_append_sheet(wb, designatedWs, "Pannes désignées");
    }

    // Feuille des top pannes
    if (topPannes.length > 0) {
      const topPannesData = topPannes.map(p => {
        const failPercentage = uniqueKO > 0 ? ((p.count / uniqueKO) * 100).toFixed(1) : '0.0';
        return {
          'Panne déclarée': p.panne,
          'Occurrences (SN uniques)': p.count,
          '% des FAIL (SN uniques)': `${failPercentage}%`
        };
      });
      
      const topWs = XLSX.utils.json_to_sheet(topPannesData);
      XLSX.utils.book_append_sheet(wb, topWs, "Top Pannes déclarées");
    }

    // Feuille des pannes Other
    if (otherPannesStats.length > 0) {
      const otherPannesData = otherPannesStats.map(p => {
        const otherPercentage = totalOtherCount > 0 ? ((p.count / totalOtherCount) * 100).toFixed(1) : '0.0';
        return {
          'Panne détectée dans "Other" (failureStep)': p.panne,
          'Occurrences': p.count,
          '% des "Other"': `${otherPercentage}%`
        };
      });
      
      const otherWs = XLSX.utils.json_to_sheet(otherPannesData);
      XLSX.utils.book_append_sheet(wb, otherWs, "Pannes Other détectées");
    }

    const buffer = XLSX.write(wb, { 
      bookType: 'xlsx', 
      type: 'array',
      bookSST: false
    });
    
    const filename = `MPSI_Export_${dateStart}_à_${dateEnd}_${mode}_SN-Uniques.xlsx`;
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), filename);

    console.log('✅ Export Excel terminé avec élimination des doublons SN');

  } catch (error) {
    console.error("Erreur lors de l'export Excel:", error);
    console.error("Stack trace:", error.stack);
    alert("Erreur lors de l'export Excel: " + error.message);
  }
};

// Composant principal JIG
const JIG = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [allSessions, setAllSessions] = useState([]);
  const [uniqueSessions, setUniqueSessions] = useState([]);
  const [topPannes, setTopPannes] = useState([]);
  const [otherPannesStats, setOtherPannesStats] = useState([]);
  const [designatedPannesStats, setDesignatedPannesStats] = useState({ stats: [], totalCount: 0, uniqueCount: 0 });
  const [testerStats, setTesterStats] = useState([]);
  const [jigTesterStats, setJigTesterStats] = useState([]);
  const [oobTesterStats, setOobTesterStats] = useState([]);
  const [cadenceStats, setCadenceStats] = useState([]);
  const [selectedTester, setSelectedTester] = useState(null);
  const [mode, setMode] = useState('JIG');
  
  const [snSearch, setSnSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [dateStart, setDateStart] = useState(() => localStorage.getItem('ds5_mpsi_dateStart') || today);
  const [dateEnd, setDateEnd] = useState(() => localStorage.getItem('ds5_mpsi_dateEnd') || today);

  const isMPSIRaspberry = useCallback((id) => {
    return typeof id === 'string' && id.includes('DS5_JIG_Tester_MPSI');
  }, []);

  const isOOBTester = useCallback((id) => {
    return typeof id === 'string' && id.includes('DS5_OOB_MPSI_');
  }, []);

  // Recherche par SN utilisant l'API getAllSessionBySn
  const searchSessionsBySN = useCallback(async (serialNumber, searchMode = 'ALL') => {
    if (!serialNumber.trim()) {
      alert("Veuillez entrer un numéro de série à rechercher");
      return;
    }
    
    setSearchLoading(true);
    
    try {
      const response = await apiService.get('/getAllSessionBySn', {
        params: {
          serial_number: serialNumber.trim(),
          mode: searchMode
        }
      });
      
      if (response.data && Array.isArray(response.data)) {
        const results = response.data;
        setSearchResults(results);
        setShowSearchResults(true);
        
        if (results.length === 0) {
          alert(`Aucune session trouvée pour le numéro de série: ${serialNumber.trim()}`);
        }
      } else {
        setSearchResults([]);
        alert(`Format de réponse inattendu de l'API`);
      }
      
    } catch (error) {
      console.error("❌ Erreur lors de la recherche par SN via API:", error);
      
      if (error.response) {
        if (error.response.status === 400) {
          alert("Numéro de série invalide. Veuillez vérifier le format.");
        } else if (error.response.status === 500) {
          alert("Erreur serveur. Veuillez réessayer plus tard.");
        } else {
          alert(`Erreur ${error.response.status}: ${error.response.data?.error || 'Erreur inconnue'}`);
        }
      } else if (error.request) {
        alert("Pas de réponse du serveur. Vérifiez votre connexion.");
      } else {
        alert("Erreur lors de la recherche. Veuillez réessayer.");
      }
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSNSearch = useCallback(async () => {
    await searchSessionsBySN(snSearch, mode);
  }, [snSearch, mode, searchSessionsBySN]);

  // Calcul des statistiques SANS DOUBLONS
  const calculateTopPannes = useCallback((sessions) => {
    const pannesMap = {};
    const seenSNs = new Set();
    
    sessions.forEach(s => {
      if (s.globalStatus !== 'KO') return;
      
      const sn = s.realSerialNumber || s.deviceSerialNumber;
      if (!sn || seenSNs.has(sn)) return;
      
      seenSNs.add(sn);
      const p = s.symptome_description || 'Inconnu';
      pannesMap[p] = (pannesMap[p] || 0) + 1;
    });
    
    return Object.entries(pannesMap)
      .map(([p, c]) => ({ panne: p, count: c }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, []);

  // MODIFIÉ: calculateTesterStats pour utiliser processed_date
  const calculateTesterStats = useCallback((sessions) => {
    const testerMap = {};
    
    sessions.forEach(s => {
      // Utiliser getSessionDate qui donne la priorité à processed_date
      if (!getSessionDate(s) || !s.realSerialNumber || !s.raspberry_id) return;
      if (/not found/i.test(s.realSerialNumber)) return;
      
      let includeTester = false;
      if (mode === 'JIG') includeTester = isMPSIRaspberry(s.raspberry_id);
      else if (mode === 'OOB') includeTester = isOOBTester(s.raspberry_id);
      else includeTester = true;
      
      if (!includeTester) return;
      
      const testerName = s.raspberry_id;
      
      if (!testerMap[testerName]) {
        testerMap[testerName] = {
          name: testerName,
          sessions: [],
          total: 0,
          ok: 0,
          ko: 0,
          topPannes: [],
          otherPannesStats: [],
          firstTest: null,
          lastTest: null
        };
      }
      
      const tester = testerMap[testerName];
      tester.sessions.push(s);
      
      const testDate = new Date(getSessionDate(s));
      if (!tester.firstTest || testDate < tester.firstTest) {
        tester.firstTest = testDate;
      }
      if (!tester.lastTest || testDate > tester.lastTest) {
        tester.lastTest = testDate;
      }
    });
    
    const testerArray = Object.values(testerMap).map(tester => {
      const uniqueSessions = filterUniqueSessionsBySN(tester.sessions);
      const totalCount = uniqueSessions.length;
      const okCount = uniqueSessions.filter(s => s.globalStatus === 'OK').length;
      const koCount = uniqueSessions.filter(s => s.globalStatus === 'KO').length;
      
      const top10 = calculateTopPannes(uniqueSessions);
      const otherStats = calculateOtherPannesFromSessions(
        uniqueSessions.filter(s => s.symptome_description === 'Other')
      );

      return {
        name: tester.name,
        total: totalCount,
        ok: okCount,
        ko: koCount,
        rate: totalCount > 0 ? parseFloat(((okCount / totalCount) * 100).toFixed(1)) : 0,
        topPannes: top10,
        otherPannesStats: otherStats,
        firstTest: tester.firstTest,
        lastTest: tester.lastTest,
        sessions: tester.sessions
      };
    });
    
    return testerArray.sort((a, b) => b.total - a.total);
  }, [mode, isMPSIRaspberry, isOOBTester, calculateTopPannes]);

  // MODIFIÉ: calculateCadenceStats pour utiliser processed_date
  const calculateCadenceStats = useCallback((sessions) => {
    const cadenceMap = {};
    const seenSNsPerOperator = new Map();
    
    sessions.forEach(s => {
      if (!getSessionDate(s) || !s.raspberry_id || !s.operator_name) return;
      
      const operator = formatOperator(s.operator_name);
      if (operator === 'n/a') return;
      
      if (!cadenceMap[operator]) {
        cadenceMap[operator] = {
          operator: operator,
          sessions: [],
          uniqueSessions: [],
          totalTests: 0,
          passCount: 0,
          failCount: 0,
          firstTest: null,
          lastTest: null,
          testers: new Set(),
          testerStats: {}
        };
        seenSNsPerOperator.set(operator, new Set());
      }
      
      const operatorData = cadenceMap[operator];
      const sn = s.realSerialNumber || s.deviceSerialNumber;
      const snSet = seenSNsPerOperator.get(operator);
      
      operatorData.sessions.push(s);
      
      if (sn && !snSet.has(sn)) {
        snSet.add(sn);
        operatorData.uniqueSessions.push(s);
        operatorData.totalTests++;
        
        if (s.globalStatus === 'OK') {
          operatorData.passCount++;
        } else if (s.globalStatus === 'KO') {
          operatorData.failCount++;
        }
      }
      
      const testerName = s.raspberry_id;
      if (testerName) {
        operatorData.testers.add(testerName);
        
        if (!operatorData.testerStats[testerName]) {
          operatorData.testerStats[testerName] = {
            testerName: testerName,
            testCount: 0,
            passCount: 0,
            failCount: 0,
            firstTest: null,
            lastTest: null
          };
        }
        
        const testerData = operatorData.testerStats[testerName];
        
        if (sn && !snSet.has(sn)) {
          testerData.testCount++;
          
          if (s.globalStatus === 'OK') {
            testerData.passCount++;
          } else if (s.globalStatus === 'KO') {
            testerData.failCount++;
          }
        }
        
        const testDate = new Date(getSessionDate(s));
        if (!testerData.firstTest || testDate < testerData.firstTest) {
          testerData.firstTest = testDate;
        }
        if (!testerData.lastTest || testDate > testerData.lastTest) {
          testerData.lastTest = testDate;
        }
      }
      
      const testDate = new Date(getSessionDate(s));
      if (!operatorData.firstTest || testDate < operatorData.firstTest) {
        operatorData.firstTest = testDate;
      }
      if (!operatorData.lastTest || testDate > operatorData.lastTest) {
        operatorData.lastTest = testDate;
      }
    });
    
    const cadenceArray = Object.values(cadenceMap).map(operatorData => {
      const { firstTest, lastTest, totalTests } = operatorData;
      
      let totalHours = 1;
      if (firstTest && lastTest) {
        const diffMs = lastTest - firstTest;
        totalHours = Math.max(diffMs / (1000 * 60 * 60), 1);
      }
      
      const cadence = totalTests / totalHours;
      const successRate = totalTests > 0 ? (operatorData.passCount / totalTests) * 100 : 0;
      
      const otherPannesSessions = operatorData.uniqueSessions.filter(s => 
        s.globalStatus === 'KO' && s.symptome_description === 'Other'
      );
      
      const otherPannesDetails = calculateOtherPannesFromSessions(otherPannesSessions);
      const totalOtherPannes = otherPannesDetails.reduce((sum, item) => sum + item.count, 0);
      
      const testersList = Array.from(operatorData.testers).map(testerName => {
        const testerData = operatorData.testerStats[testerName] || {};
        return {
          testerName,
          testCount: testerData.testCount || 0
        };
      }).sort((a, b) => b.testCount - a.testCount);
      
      const testerDetails = Object.values(operatorData.testerStats)
        .map(tester => ({
          ...tester,
          successRate: tester.testCount > 0 
            ? parseFloat(((tester.passCount / tester.testCount) * 100).toFixed(1))
            : 0
        }))
        .sort((a, b) => b.testCount - a.testCount);
      
      return {
        operator: operatorData.operator,
        totalTests,
        passCount: operatorData.passCount,
        failCount: operatorData.failCount,
        successRate: parseFloat(successRate.toFixed(1)),
        cadence: parseFloat(cadence.toFixed(2)),
        totalHours: parseFloat(totalHours.toFixed(1)),
        firstTest: operatorData.firstTest,
        lastTest: operatorData.lastTest,
        otherPannes: totalOtherPannes,
        testers: Array.from(operatorData.testers),
        testersList: testersList,
        testerDetails: testerDetails,
        sessions: operatorData.sessions
      };
    });
    
    return cadenceArray.sort((a, b) => b.cadence - a.cadence);
  }, []);

  // FONCTION PRINCIPALE DE RÉCUPÉRATION DES DONNÉES
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setAllSessions([]);
      setUniqueSessions([]);
      setTopPannes([]);
      setOtherPannesStats([]);
      setDesignatedPannesStats({ stats: [], totalCount: 0, uniqueCount: 0 });
      setTesterStats([]);
      setJigTesterStats([]);
      setOobTesterStats([]);
      setCadenceStats([]);
      setSelectedTester(null);
      setSearchResults([]);
      setShowSearchResults(false);

      const startDateWithTime = addTimeToDate(dateStart, true);
      const endDateWithTime = addTimeToDate(dateEnd, false);
      
      const timestamp = new Date().getTime();

      let response;
      try {
        const apiUrl = '/getDataSessionMPSI/';
        const params = {
          'dateRange[]': [startDateWithTime, endDateWithTime],
          mode: mode,
          page: 1,
          pageSize: -1,
          _t: timestamp
        };
        
        response = await apiService.get(apiUrl, { params });
        
        if (!response.data) {
          throw new Error("Pas de réponse de l'API");
        }
        
      } catch (apiError) {
        console.error("Erreur API getDataSessionMPSI:", apiError);
        alert("Erreur de connexion à l'API: " + (apiError.message || "Vérifiez la connexion"));
        return;
      }

      const apiData = response.data.data || [];
      
      // Filtrer selon le mode
      let filteredSessions = apiData.filter(s => {
        if (!s || !s.raspberry_id) return false;
        if (mode === 'JIG') return isMPSIRaspberry(s.raspberry_id);
        if (mode === 'OOB') return isOOBTester(s.raspberry_id);
        return true;
      });

      setAllSessions(filteredSessions);
      
      // Créer une liste sans doublons SN (utilise processed_date pour le tri)
      const uniqueSessionsList = filterUniqueSessionsBySN(filteredSessions);
      setUniqueSessions(uniqueSessionsList);

      // Calculer les statistiques SANS DOUBLONS
      const topPannesResult = calculateTopPannes(uniqueSessionsList);
      const otherPannesResult = calculateOtherPannesFromSessions(uniqueSessionsList);
      const designatedPannesResult = calculateDesignatedPannesStats(uniqueSessionsList);
      const testerStatsResult = calculateTesterStats(filteredSessions);
      const cadenceStatsResult = calculateCadenceStats(filteredSessions);

      setTopPannes(topPannesResult);
      setOtherPannesStats(otherPannesResult);
      setDesignatedPannesStats(designatedPannesResult);
      setTesterStats(testerStatsResult);
      setCadenceStats(cadenceStatsResult);

      // Calculer les statistiques par type de testeur
      const jigSessions = apiData.filter(s => isMPSIRaspberry(s.raspberry_id));
      const oobSessions = apiData.filter(s => isOOBTester(s.raspberry_id));
      
      setJigTesterStats(calculateTesterStats(jigSessions));
      setOobTesterStats(calculateTesterStats(oobSessions));

      console.log('✅ Données chargées avec élimination des doublons SN');
      console.log(`Total sessions: ${filteredSessions.length}`);
      console.log(`Sessions uniques (sans doublons SN): ${uniqueSessionsList.length}`);
      console.log(`Tri effectué sur: processed_date (ou startDate si processed_date n'existe pas)`);

    } catch (err) {
      console.error("Erreur fetchData:", err);
      alert("Erreur de chargement des données: " + (err.message || err.toString()));
    } finally {
      setLoading(false);
    }
  }, [dateStart, dateEnd, mode, isMPSIRaspberry, isOOBTester, calculateTopPannes, calculateTesterStats, calculateCadenceStats]);

  const exportToExcel = useCallback(() => {
    exportToExcelWithPanneDescriptions(
      allSessions,
      uniqueSessions,
      mode,
      dateStart,
      dateEnd,
      topPannes,
      otherPannesStats,
      designatedPannesStats,
      otherPannesStats.reduce((sum, item) => sum + item.count, 0)
    );
  }, [allSessions, uniqueSessions, mode, dateStart, dateEnd, topPannes, otherPannesStats, designatedPannesStats]);

  useEffect(() => {
    localStorage.setItem('ds5_mpsi_dateStart', dateStart);
    localStorage.setItem('ds5_mpsi_dateEnd', dateEnd);
  }, [dateStart, dateEnd]);

  // Gestion de la touche Entrée pour la recherche SN
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && snSearch.trim() && !searchLoading) {
        handleSNSearch();
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [snSearch, searchLoading, handleSNSearch]);

  const uniqueTotal = uniqueSessions.length;
  const uniqueOK = uniqueSessions.filter(s => s.globalStatus === 'OK').length;
  const uniqueKO = uniqueSessions.filter(s => s.globalStatus === 'KO').length;
  const uniqueOKRate = uniqueTotal > 0 ? ((uniqueOK / uniqueTotal) * 100).toFixed(1) : 0;
  
  const totalOtherCount = useMemo(() => 
    otherPannesStats.reduce((sum, item) => sum + item.count, 0), 
    [otherPannesStats]
  );
  const otherRate = uniqueKO > 0 ? ((totalOtherCount / uniqueKO) * 100).toFixed(1) : 0;

  const doughnutData = useMemo(() => ({ 
    labels: ['PASS', 'FAIL'], 
    datasets: [{ 
      data: [uniqueOK, uniqueKO], 
      backgroundColor: ['#10b981', '#ef4444'], 
      borderWidth: 2,
      borderColor: '#ffffff'
    }] 
  }), [uniqueOK, uniqueKO]);
  
  const topPannesData = useMemo(() => ({ 
    labels: topPannes.map(p => p.panne), 
    datasets: [{ 
      data: topPannes.map(p => p.count), 
      backgroundColor: '#3b82f6',
      borderRadius: 4,
      borderWidth: 0
    }] 
  }), [topPannes]);
  
  const otherPannesData = useMemo(() => ({ 
    labels: otherPannesStats.map(p => p.panne), 
    datasets: [{ 
      data: otherPannesStats.map(p => p.count), 
      backgroundColor: '#f59e0b',
      borderRadius: 4,
      borderWidth: 0
    }] 
  }), [otherPannesStats]);
  
  const designatedPannesData = useMemo(() => ({ 
    labels: designatedPannesStats.stats.slice(0, 15).map(p => p.panne), 
    datasets: [{ 
      label: 'Occurrences',
      data: designatedPannesStats.stats.slice(0, 15).map(p => p.count), 
      backgroundColor: '#8b5cf6',
      borderRadius: 4,
      borderWidth: 0
    }] 
  }), [designatedPannesStats]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { size: 12 },
          padding: 15,
          usePointStyle: true,
          boxWidth: 8
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        padding: 8,
        cornerRadius: 4,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    cutout: '70%'
  }), []);

  const barOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        padding: 8,
        cornerRadius: 4
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { font: { size: 11 } }
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, maxRotation: 45 }
      }
    }
  }), []);

  return (
    <div className="jig-container">
      <div className="jig-header">
        <div className="header-content">
          <div>
            <h1 className="header-title">Dashboard DS5 MPSI</h1>
            <p className="header-subtitle">
              {dateStart === dateEnd 
                ? new Date(dateStart).toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })
                : `Du ${new Date(dateStart).toLocaleDateString('fr-FR')} au ${new Date(dateEnd).toLocaleDateString('fr-FR')}`}
              <br/>
              <small style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                Statistiques calculées sans doublons de SN - Tri par processed_date
              </small>
            </p>
          </div>
          
          <div className="header-controls">
            <select 
              value={mode} 
              onChange={e => setMode(e.target.value)}
              className="mode-select"
            >
              <option value="JIG">Mode JIG</option>
              <option value="OOB">Mode OOB</option>
              <option value="ALL">Tous les modes</option>
            </select>
            
            <div className="date-range">
              <input 
                type="date" 
                value={dateStart} 
                onChange={e => setDateStart(e.target.value)} 
                className="date-input"
              />
              <span className="date-separator">à</span>
              <input 
                type="date" 
                value={dateEnd} 
                onChange={e => setDateEnd(e.target.value)} 
                className="date-input"
              />
            </div>
            
            <div className="sn-search-container">
              <input
                type="text"
                value={snSearch}
                onChange={(e) => setSnSearch(e.target.value)}
                placeholder="Rechercher par SN..."
                className="sn-search-input"
                disabled={searchLoading}
              />
              <button
                onClick={handleSNSearch}
                disabled={searchLoading || !snSearch.trim()}
                className={`sn-search-button ${searchLoading ? 'loading' : ''}`}
              >
                🔍 {searchLoading ? 'Recherche...' : 'Rechercher SN'}
              </button>
            </div>
            
            <button 
              onClick={fetchData} 
              disabled={loading}
              className={`search-button ${loading ? 'loading' : ''}`}
            >
              🔍 {loading ? 'Chargement...' : 'Rechercher'}
            </button>
            
            <button 
              onClick={exportToExcel} 
              disabled={allSessions.length === 0}
              className={`export-button ${allSessions.length === 0 ? 'disabled' : ''}`}
            >
              📥 Export
            </button>
          </div>
        </div>
      </div>

      <div className="navigation-container">
        <div className="nav-tabs">
          <button 
            onClick={() => setActiveTab('summary')} 
            className={`nav-tab ${activeTab === 'summary' ? 'active' : ''}`}
          >
            📊 Résumé
          </button>
          
          <button 
            onClick={() => setActiveTab('pannes')} 
            className={`nav-tab ${activeTab === 'pannes' ? 'active' : ''}`}
          >
            ⚠️ Top Pannes
            {uniqueKO > 0 && <span className="tab-badge">{topPannes.length}</span>}
          </button>
          
          <button 
            onClick={() => setActiveTab('designated')} 
            className={`nav-tab ${activeTab === 'designated' ? 'active' : ''}`}
          >
            📋 Pannes Désignées
            {designatedPannesStats.uniqueCount > 0 && <span className="tab-badge designated">{designatedPannesStats.uniqueCount}</span>}
          </button>
          
          <button 
            onClick={() => setActiveTab('other')} 
            className={`nav-tab ${activeTab === 'other' ? 'active' : ''}`}
          >
            🔧 Pannes "Other"
            {totalOtherCount > 0 && <span className="tab-badge other">{totalOtherCount}</span>}
          </button>
          
          <button 
            onClick={() => setActiveTab('cadence')} 
            className={`nav-tab ${activeTab === 'cadence' ? 'active' : ''}`}
          >
            ⏱️ Cadence Opérateurs
            {cadenceStats.length > 0 && <span className="tab-badge cadence">{cadenceStats.length}</span>}
          </button>
          
          {(mode === 'JIG' || mode === 'ALL') && (
            <button 
              onClick={() => setActiveTab('qualityJIG')} 
              className={`nav-tab ${activeTab === 'qualityJIG' ? 'active' : ''}`}
            >
              👥 Performance JIG
            </button>
          )}
          
          {(mode === 'OOB' || mode === 'ALL') && (
            <button 
              onClick={() => setActiveTab('qualityOOB')} 
              className={`nav-tab ${activeTab === 'qualityOOB' ? 'active' : ''}`}
            >
              👥 Performance OOB
            </button>
          )}
        </div>
      </div>

      <div className="content-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <div className="loading-text">Chargement des données...</div>
          </div>
        ) : allSessions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <div className="empty-title">Aucune donnée disponible</div>
            <div className="empty-subtitle">Sélectionnez une période et cliquez sur "Rechercher"</div>
          </div>
        ) : (
          <>
            {activeTab === 'summary' && (
              <SummaryStats 
                uniqueSessions={uniqueSessions} 
                allSessions={allSessions}
                designatedPannesStats={designatedPannesStats}
                totalOtherCount={totalOtherCount}
              />
            )}

            {activeTab === 'pannes' && (
              <div className="pannes-container">
                <div className="section-title">
                  Top 10 des Pannes Déclarées (SN uniques)
                  <div className="section-subtitle">
                    Calculé sans doublons de numéros de série - Tri par processed_date
                  </div>
                </div>
                
                {topPannes.length === 0 ? (
                  <div className="no-data">Aucune panne sur cette période</div>
                ) : (
                  <>
                    <div className="chart-wrapper bar">
                      <Bar data={topPannesData} options={barOptions} />
                    </div>
                    
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Panne</th>
                            <th className="text-right">Occurrences (SN uniques)</th>
                            <th className="text-right">% des FAIL (SN uniques)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topPannes.map((item, index) => {
                            const failPercentage = uniqueKO > 0 ? ((item.count / uniqueKO) * 100).toFixed(1) : '0.0';
                            return (
                              <tr key={index} className={index % 2 === 0 ? 'even' : 'odd'}>
                                <td>{item.panne}</td>
                                <td className="text-right">{item.count}</td>
                                <td className="text-right percentage">{failPercentage}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'designated' && (
              <div className="designated-container">
                <div className="section-title">
                  Pannes Désignées (panne_descriptions)
                  <div className="section-subtitle">
                    Calculé sans doublons de numéros de série - Tri par processed_date
                  </div>
                </div>
                
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-label">Sessions FAIL (SN uniques)</div>
                    <div className="stat-value fail">{uniqueKO}</div>
                  </div>
                  
                  <div className="stat-card designated">
                    <div className="stat-label">Pannes désignées total</div>
                    <div className="stat-value">{designatedPannesStats.totalCount}</div>
                  </div>
                  
                  <div className="stat-card designated">
                    <div className="stat-label">Types de pannes</div>
                    <div className="stat-value">{designatedPannesStats.uniqueCount}</div>
                  </div>
                </div>
                
                {designatedPannesStats.stats.length === 0 ? (
                  <div className="no-data">Aucune panne désignée trouvée</div>
                ) : (
                  <>
                    <div className="chart-wrapper bar">
                      <Bar data={designatedPannesData} options={barOptions} />
                    </div>
                    
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Panne Désignée</th>
                            <th className="text-right">Occurrences (SN uniques)</th>
                            <th className="text-right">% des FAIL (SN uniques)</th>
                            <th className="text-right">Dont "Other"</th>
                            <th className="text-right">% "Other"</th>
                          </tr>
                        </thead>
                        <tbody>
                          {designatedPannesStats.stats.map((item, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'even' : 'odd'}>
                              <td>{item.panne}</td>
                              <td className="text-right">{item.count}</td>
                              <td className="text-right percentage">{item.percentage}%</td>
                              <td className="text-right other-count">{item.otherCount}</td>
                              <td className="text-right percentage">{item.otherPercentage}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'other' && (
              <div className="other-container">
                <div className="section-title">
                  Pannes "Other" Détectées - Analyse du Failure Step
                  <div className="section-subtitle">
                    {totalOtherCount} occurrences (calculé sans doublons SN - Tri par processed_date)
                  </div>
                </div>
                
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-label">Sessions FAIL (SN uniques)</div>
                    <div className="stat-value fail">{uniqueKO}</div>
                  </div>
                  
                  <div className="stat-card other">
                    <div className="stat-label">Pannes "Other" détectées</div>
                    <div className="stat-value">{totalOtherCount}</div>
                  </div>
                  
                  <div className="stat-card other">
                    <div className="stat-label">Taux "Other" détectées</div>
                    <div className="stat-value">{otherRate}%</div>
                    <div className="stat-subtext">des sessions FAIL</div>
                  </div>
                </div>
                
                {otherPannesStats.length === 0 ? (
                  <div className="no-data other">
                    Aucune panne "Other" détectée dans le failure step
                  </div>
                ) : (
                  <>
                    <div className="chart-wrapper bar">
                      <Bar data={otherPannesData} options={barOptions} />
                    </div>
                    
                    <div className="table-container">
                      <table className="data-table other">
                        <thead>
                          <tr>
                            <th>Panne détectée dans "Other" (failureStep)</th>
                            <th className="text-right">Occurrences</th>
                            <th className="text-right">% des "Other"</th>
                          </tr>
                        </thead>
                        <tbody>
                          {otherPannesStats.map((item, index) => {
                            const otherPercentage = totalOtherCount > 0 
                              ? ((item.count / totalOtherCount) * 100).toFixed(1)
                              : 0;
                            
                            return (
                              <tr key={index} className={index % 2 === 0 ? 'even' : 'odd'}>
                                <td>{item.panne}</td>
                                <td className="text-right">{item.count}</td>
                                <td className="text-right percentage">{otherPercentage}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'cadence' && (
              <div className="cadence-container">
                <CadenceTable cadenceStats={cadenceStats} />
              </div>
            )}

            {activeTab === 'qualityJIG' && (
              <div className="quality-container">
                <div className="section-title">Performance des Testeurs JIG (SN uniques - Tri par processed_date)</div>
                
                {jigTesterStats.length === 0 ? (
                  <div className="no-data">Aucun testeur JIG trouvé</div>
                ) : (
                  <>
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Testeur</th>
                            <th className="text-right">Total (SN uniques)</th>
                            <th className="text-right">PASS</th>
                            <th className="text-right">FAIL</th>
                            <th className="text-right">Taux</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jigTesterStats.map((tester, index) => (
                            <tr 
                              key={index}
                              className={`${index % 2 === 0 ? 'even' : 'odd'} clickable`}
                              onClick={() => setSelectedTester(tester)}
                            >
                              <td>{tester.name}</td>
                              <td className="text-right">{tester.total}</td>
                              <td className="text-right pass">{tester.ok}</td>
                              <td className="text-right fail">{tester.ko}</td>
                              <td className="text-right">
                                <span className={`rate-badge ${
                                  tester.rate >= 95 ? 'high' : 
                                  tester.rate >= 90 ? 'medium' : 
                                  'low'
                                }`}>
                                  {tester.rate}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'qualityOOB' && (
              <div className="quality-container">
                <div className="section-title">Performance des Testeurs OOB (SN uniques - Tri par processed_date)</div>
                
                {oobTesterStats.length === 0 ? (
                  <div className="no-data">Aucun testeur OOB trouvé</div>
                ) : (
                  <>
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Testeur</th>
                            <th className="text-right">Total (SN uniques)</th>
                            <th className="text-right">PASS</th>
                            <th className="text-right">FAIL</th>
                            <th className="text-right">Taux</th>
                          </tr>
                        </thead>
                        <tbody>
                          {oobTesterStats.map((tester, index) => (
                            <tr 
                              key={index}
                              className={`${index % 2 === 0 ? 'even' : 'odd'} clickable`}
                              onClick={() => setSelectedTester(tester)}
                            >
                              <td>{tester.name}</td>
                              <td className="text-right">{tester.total}</td>
                              <td className="text-right pass">{tester.ok}</td>
                              <td className="text-right fail">{tester.ko}</td>
                              <td className="text-right">
                                <span className={`rate-badge ${
                                  tester.rate >= 95 ? 'high' : 
                                  tester.rate >= 90 ? 'medium' : 
                                  'low'
                                }`}>
                                  {tester.rate}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {selectedTester && (
        <TesterDetailsModal
          tester={selectedTester}
          onClose={() => setSelectedTester(null)}
        />
      )}

      {showSearchResults && (
        <SNSearchModal
          searchResults={searchResults}
          onClose={() => setShowSearchResults(false)}
        />
      )}

      <style jsx>{`
        /* ... (le CSS reste exactement le même que dans votre code précédent) ... */
        .jig-container {
          min-height: 100vh;
          background-color: #f9fafb;
          font-family: 'Segoe UI', 'Roboto', sans-serif;
        }

        .jig-header {
          background-color: #ffffff;
          border-bottom: 1px solid #e5e7eb;
          padding: 1.5rem 1rem;
        }

        .header-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .header-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }

        .header-subtitle {
          color: #6b7280;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          margin-bottom: 0;
        }

        .header-controls {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.75rem;
        }

        .mode-select, .date-input, .sn-search-input {
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.2s;
        }

        .mode-select:focus, .date-input:focus, .sn-search-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .mode-select {
          min-width: 120px;
        }

        .date-input {
          width: 8rem;
        }

        .date-range {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .date-separator {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .sn-search-container {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex: 1;
          max-width: 400px;
        }

        .sn-search-input {
          flex: 1;
          min-width: 200px;
        }

        .sn-search-button, .search-button, .export-button {
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .sn-search-button {
          background-color: #8b5cf6;
          color: #ffffff;
        }

        .sn-search-button:hover:not(:disabled) {
          background-color: #7c3aed;
        }

        .sn-search-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .sn-search-button.loading {
          position: relative;
        }

        .sn-search-button.loading::after {
          content: '';
          position: absolute;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .search-button {
          background-color: #3b82f6;
          color: #ffffff;
        }

        .search-button:hover:not(:disabled) {
          background-color: #2563eb;
        }

        .search-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .search-button.loading {
          position: relative;
        }

        .search-button.loading::after {
          content: '';
          position: absolute;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .export-button {
          background-color: #10b981;
          color: #ffffff;
        }

        .export-button:hover:not(.disabled) {
          background-color: #059669;
        }

        .export-button.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .navigation-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1.5rem 1rem;
        }

        .nav-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .nav-tab {
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          border: 1px solid #d1d5db;
          background-color: #ffffff;
          color: #374151;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
          position: relative;
        }

        .nav-tab:hover {
          background-color: #f9fafb;
          border-color: #9ca3af;
        }

        .nav-tab.active {
          background-color: #3b82f6;
          color: #ffffff;
          border-color: #3b82f6;
        }

        .tab-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background-color: #3b82f6;
          color: #ffffff;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 10px;
          min-width: 20px;
          text-align: center;
        }

        .tab-badge.other {
          background-color: #f59e0b;
        }

        .tab-badge.cadence {
          background-color: #8b5cf6;
        }

        .tab-badge.designated {
          background-color: #8b5cf6;
        }

        .content-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1rem 3rem;
        }

        .loading-state, .empty-state {
          background-color: #ffffff;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          padding: 3rem;
          text-align: center;
        }

        .spinner {
          display: inline-block;
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-text {
          color: #6b7280;
          font-size: 1rem;
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
          color: #9ca3af;
        }

        .empty-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .empty-subtitle {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .summary-container {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .stat-card {
          background-color: #ffffff;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          padding: 1.5rem;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .stat-card.pass {
          border-left: 4px solid #10b981;
        }

        .stat-card.fail {
          border-left: 4px solid #ef4444;
        }

        .stat-card.other {
          border-left: 4px solid #f59e0b;
        }

        .stat-card.designated {
          border-left: 4px solid #8b5cf6;
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .stat-label {
          color: #6b7280;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .stat-icon {
          font-size: 1.25rem;
        }

        .stat-value {
          font-size: 1.875rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0.5rem;
        }

        .stat-card.pass .stat-value {
          color: #10b981;
        }

        .stat-card.fail .stat-value {
          color: #ef4444;
        }

        .stat-card.other .stat-value {
          color: #f59e0b;
        }

        .stat-card.designated .stat-value {
          color: #8b5cf6;
        }

        .stat-details {
          margin-top: 0.5rem;
        }

        .stat-percentage {
          font-size: 1.25rem;
          font-weight: 600;
          color: #10b981;
        }

        .stat-subtext {
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }

        .stat-description {
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.5rem;
        }

        .other-count {
          color: #f59e0b;
          font-weight: 600;
        }

        .chart-container, .pannes-container, .designated-container, .other-container, .quality-container, .cadence-container {
          background-color: #ffffff;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          padding: 1.5rem;
        }

        .designated-summary {
          background-color: #f8fafc;
          border-radius: 0.75rem;
          padding: 1.5rem;
          border: 1px solid #e5e7eb;
        }

        .chart-wrapper {
          height: 320px;
        }

        .chart-wrapper.bar {
          height: 384px;
          margin-bottom: 1.5rem;
        }

        .section-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .section-subtitle {
          font-size: 0.875rem;
          color: #6b7280;
          margin-top: 0.25rem;
          font-weight: normal;
          text-align: center;
        }

        .table-container {
          overflow-x: auto;
          border-radius: 0.5rem;
          border: 1px solid #e5e7eb;
        }

        .data-table {
          width: 100%;
          font-size: 0.875rem;
          border-collapse: collapse;
        }

        .data-table thead {
          background-color: #f9fafb;
        }

        .data-table th {
          padding: 0.75rem 1rem;
          text-align: left;
          font-weight: 500;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        .data-table tbody tr {
          border-bottom: 1px solid #e5e7eb;
        }

        .data-table tr.even {
          background-color: #ffffff;
        }

        .data-table tr.odd {
          background-color: #f9fafb;
        }

        .data-table td {
          padding: 0.75rem 1rem;
          color: #111827;
        }

        .text-right {
          text-align: right;
        }

        .pass {
          color: #10b981;
          font-weight: 500;
        }

        .fail {
          color: #ef4444;
          font-weight: 500;
        }

        .percentage {
          color: #3b82f6;
          font-weight: 500;
        }

        .other-cell {
          font-size: 0.8rem;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 50;
          padding: 1rem;
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          background-color: #ffffff;
          border-radius: 0.75rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow: auto;
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .modal-content.wide {
          max-width: 95%;
        }

        .modal-header {
          padding: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: sticky;
          top: 0;
          background-color: #ffffff;
          z-index: 10;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }

        .modal-subtitle {
          color: #6b7280;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          margin-bottom: 0;
        }

        .modal-close {
          padding: 0.5rem;
          border-radius: 0.5rem;
          background-color: transparent;
          border: none;
          cursor: pointer;
          color: #6b7280;
          font-size: 1.25rem;
          transition: background-color 0.2s;
        }

        .modal-close:hover {
          background-color: #f3f4f6;
        }

        .modal-body {
          padding: 1.5rem;
        }

        .search-stats {
          margin-bottom: 1.5rem;
          padding: 1rem;
          background-color: #f8fafc;
          border-radius: 0.5rem;
          border: 1px solid #e2e8f0;
        }

        .stats-summary {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          justify-content: center;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
        }

        .stat-value {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
        }

        .tester-details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .detail-section {
          background-color: #f9fafb;
          border-radius: 0.5rem;
          padding: 1rem;
          border: 1px solid #e5e7eb;
        }

        .detail-section h3 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.75rem;
        }

        .detail-section.full-width {
          grid-column: 1 / -1;
        }

        .data-table.small {
          font-size: 0.75rem;
        }

        .data-table.small th,
        .data-table.small td {
          padding: 0.5rem 0.75rem;
        }

        .no-data {
          text-align: center;
          color: #6b7280;
          padding: 2rem;
          font-style: italic;
          font-size: 0.875rem;
        }

        .no-data.other {
          color: #f59e0b;
        }

        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .status-badge.pass {
          background-color: #d1fae5;
          color: #065f46;
        }

        .status-badge.fail {
          background-color: #fee2e2;
          color: #991b1b;
        }

        .rate-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .rate-badge.high {
          background-color: #d1fae5;
          color: #065f46;
        }

        .rate-badge.medium {
          background-color: #fef3c7;
          color: #92400e;
        }

        .rate-badge.low {
          background-color: #fee2e2;
          color: #991b1b;
        }

        .cadence-value {
          font-weight: 600;
          color: #111827;
        }

        .cadence-subtext {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: normal;
        }

        .testers-count {
          position: relative;
          cursor: help;
        }

        .testers-count:hover::after {
          content: attr(data-testers);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background-color: #1f2937;
          color: #ffffff;
          padding: 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          white-space: nowrap;
          z-index: 100;
        }

        .clickable {
          cursor: pointer;
        }

        .clickable:hover {
          background-color: #f3f4f6 !important;
        }

        @media (max-width: 768px) {
          .header-controls {
            flex-direction: column;
          }
          
          .mode-select,
          .date-range,
          .sn-search-container,
          .search-button,
          .export-button {
            width: 100%;
          }
          
          .date-range {
            justify-content: space-between;
          }
          
          .date-input {
            flex: 1;
          }
          
          .nav-tabs {
            overflow-x: auto;
            padding-bottom: 0.5rem;
            flex-wrap: nowrap;
          }
          
          .nav-tab {
            white-space: nowrap;
          }
          
          .stats-grid {
            grid-template-columns: 1fr;
          }
          
          .tester-details-grid {
            grid-template-columns: 1fr;
          }
          
          .modal-content.wide {
            max-width: 100%;
            margin: 1rem;
          }
          
          .other-cell {
            max-width: 100px;
            font-size: 0.7rem;
          }
        }

        @media (max-width: 480px) {
          .header-title {
            font-size: 1.25rem;
          }
          
          .header-subtitle {
            font-size: 0.75rem;
          }
          
          .nav-tab {
            padding: 0.5rem 0.75rem;
            font-size: 0.75rem;
          }
          
          .stat-card {
            padding: 1rem;
          }
          
          .stat-value {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default JIG;